import { useEffect } from 'react';
import WalletAlpha from '../services/walletAlpha/walletAlpha';
import useLogger from '../services/logger/useLogger';
import { camelToSnake } from './common/common';

export const useOnMessage = <T>(
  message: WalletAlphaMessage,
  messageHandler: (
    messageData: WalletAlphaMessageData<T>,
    reply: ReplyCallback,
    replyError: ReplyErrorCallback,
    origin: string
  ) => Promise<void>
) => {
  const { error: reportError } = useLogger();

  useEffect(() => {
    const messageHandlerWrapper = (event: MessageEvent) => {
      const messageData = WalletAlpha.parseMessage<T>(event);
      if (messageData && messageData.message === message) {
        const reply: ReplyCallback = (options) => {
          WalletAlpha.postMessage(
            options?.customMessage ?? message,
            options?.data || {},
            event.origin
          );
        };

        const replyError: ReplyErrorCallback = ({ error, customMessage }) => {
          WalletAlpha.postErrorForMessage(customMessage ?? message, error, event.origin);
        };

        messageHandler(messageData, reply, replyError, event.origin).catch((error) =>
          reportError(`Error handling message ${message}`, error)
        );
      }
    };
    window.addEventListener('message', messageHandlerWrapper);
    return () => window.removeEventListener('message', messageHandlerWrapper);
  }, []);
};

export const useRequestMessage = <T, U>(
  message: WalletAlphaMessage,
  makeRequest: (data: T) => Promise<U>
) => {
  useOnMessage<T>(message, async ({ data }, reply, replyError) => {
    try {
      const response = await makeRequest(data);
      reply({ data: camelToSnake(response) });
    } catch (error: unknown) {
      replyError({ error });
    }
  });
};

export const useOnlyInIframe = () => window.self !== window.top;

export const useDecorator = <T, U>(decorateImplementation: (data: T) => U) => {
  const decorateWrap = (dataToDecorate: T): U & { object: T } => ({
    ...decorateImplementation(dataToDecorate),
    object: dataToDecorate,
  });

  return {
    decorate: decorateWrap,
    decorateCollection: (collection: T[]) => collection.map((element) => decorateWrap(element)),
  };
};

const SHORT_DELAY_MS = 300;
export const useDelayed =
  (callback: () => void): (() => void) =>
  () =>
    setTimeout(callback, SHORT_DELAY_MS);
